import {TypedEventTarget} from '../../logics/TypedEventTarget';
import {HEL_AUDIO_TYPE_VOICE, HEL_AUDIO_TYPE_VIDEO} from '../../logics/const';
import {RealMixer} from '../../logics/globals';
import {
  closeLiveLocalStream,
  getLiveLocalStream,
  setLiveLocalStream
} from '../../logics/localStream';
import {setLiveMicEnabled} from '../../logics/shared';
import {GetIHeliNetwork} from '../../singleton_registry';
import AgoraClient, {ensureAgoraLibraryLoadedAndInitialized} from '../AgoraClient';
import {
  onNetLiveConnected,
  onNetLiveDisconnected,
  onNetLiveRemoteJoin,
  onNetLiveRemoteLeave
} from '../networkCallback';

import type {
  IHeliLiveChannelProvider,
  LiveChannelProviderEventMap
} from './IHeliLiveChannelProvider';
import type {CHeliLiveChannel} from '../CHeliLiveChannel';
import type {CHeliPlayer} from '../CHeliPlayer';

/**
 * ブラウザコンソールからのデバッグのために、グローバル変数へ保存する。
 * @remarks リファクタリング未完了に依る一時的な `export`。
 * @see https://discord.com/channels/596169759045976094/1150648243387125790/1326351292033138771
 */
export const registerAgoraLiveClientAsGlobalVariableForDebug = (agoraLiveClient: AgoraClient) => {
  Object.assign(globalThis, {agoraLiveClient});
};

export class CHeliAgoraLiveChannelProvider
  extends TypedEventTarget<LiveChannelProviderEventMap>
  implements IHeliLiveChannelProvider
{
  #agoraLiveClient: AgoraClient | null = null;

  #localAgoraTrack: Awaited<ReturnType<InstanceType<typeof AgoraClient>['initLocalTrack']>> = {
    audioTrack: null,
    videoTrack: null
  };

  async ensureLibraryLoadedAndInitialized(): Promise<void> {
    await ensureAgoraLibraryLoadedAndInitialized();
  }

  initLiveClient(): void {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    console.debug('initLiveClient start');
    const agoraLiveClient = this.#getLiveClient();

    console.debug('initLiveClient agoraLiveClient', agoraLiveClient);

    agoraLiveClient.onConnected((localUser) => {
      onNetLiveConnected();
    });
    agoraLiveClient.onDisconnected(() => {
      onNetLiveDisconnected();
    });

    agoraLiveClient.importEngineMasterVolume(
      RealMixer,
      globalThis.g_hel_MixerMute ?? null,
      HEL_AUDIO_TYPE_VOICE,
      HEL_AUDIO_TYPE_VIDEO
    );
    agoraLiveClient.startObserveVolumeSetting();
    agoraLiveClient.on('subscribePlayerAudio', ({user}) => {
      console.debug('subscribePlayerAudio user:', user.hasAudio);
      if (user.audioTrack) {
        user.audioTrack.play();
      }
    });
    agoraLiveClient.on('stopRemoteVideoLiveStream', ({user, remoteVideoLiveStream}) => {
      console.debug('live streaming stopped', user, remoteVideoLiveStream);
      this.dispatchEvent<'videostreamchanged'>(
        new CustomEvent('videostreamchanged', {detail: null})
      );
    });
    agoraLiveClient.on('startRemoteVideoLiveStream', ({user, remoteVideoLiveStream}) => {
      console.debug('live streaming start startRemoteVideoLiveStream', user, remoteVideoLiveStream);
      this.dispatchEvent<'videostreamchanged'>(
        new CustomEvent('videostreamchanged', {detail: remoteVideoLiveStream})
      );
    });
    agoraLiveClient.on('updateRemoteVideoLiveStream', ({user, remoteVideoLiveStream}) => {
      console.debug(
        'live streaming update updateRemoteVideoLiveStream',
        user,
        remoteVideoLiveStream
      );
      this.dispatchEvent<'videostreamchanged'>(
        new CustomEvent('videostreamchanged', {detail: remoteVideoLiveStream})
      );
    });
    window.addEventListener('beforeunload', () => {
      closeLiveLocalStream();
      agoraLiveClient.close();
    });
    // 他プレーヤーjoin自
    agoraLiveClient.onUserJoined((agoraUser) => {
      console.debug('agoraLiveClient onUserJoined', agoraUser);
      const player = heliNetwork.findPlayerByRtcUserCode(
        typeof agoraUser.uid === 'number' ? agoraUser.uid : Number.parseInt(agoraUser.uid)
      );
      if (!player) {
        console.error('no player agoraUser.uid', agoraUser.uid);
        return;
      }
      // player.setAgoraUser(agoraUser);

      // ライブチャンネルへのJoinを通知
      onNetLiveRemoteJoin(player.getPlayerId());
    });
    agoraLiveClient.onUserLeft((agoraUser) => {
      console.debug('agoraLiveClient onUserLeft', agoraUser);
      const player =
        typeof agoraUser.uid === 'number' ? this.#getPlayerByRtcUserCode(agoraUser.uid) : null;
      if (!player) {
        console.error('no player agoraUser.uid', agoraUser.uid);
        return;
      }
      // ライブチャンネルからのLeaveを通知
      onNetLiveRemoteLeave(player.getPlayerId());
    });
    console.debug('initLiveClient done');
  }
  #getPlayerByRtcUserCode(rtcUserCode: number): CHeliPlayer | null {
    const heliNetwork = GetIHeliNetwork();
    if (!heliNetwork) {
      throw new Error('GetIHeliNetwork() returns null.');
    }

    for (const player of heliNetwork.getPlayerList().values()) {
      if (player.getRtcUserCode() === rtcUserCode) {
        return player;
      }
    }
    return null;
  }
  #getLiveClient(): AgoraClient {
    if (!this.#agoraLiveClient) {
      this.#agoraLiveClient = new AgoraClient({
        mode: 'live',
        ctrlRemoteVideoLiveStreamVolume: true,
        ctrlPlayerVoiceVolume: true
      });
      registerAgoraLiveClientAsGlobalVariableForDebug(this.#agoraLiveClient);
    }
    return this.#agoraLiveClient;
  }

  async setLiveMicEnabled(enabled: boolean): Promise<void> {
    if (this.#agoraLiveClient) {
      this.#agoraLiveClient.setMicMuted(!enabled);
    }
  }

  getLiveMicEnabled(): boolean {
    if (!this.#agoraLiveClient) {
      return false;
    }
    if (this.#agoraLiveClient.isMicMuted()) {
      return false;
    }
    return !this.#agoraLiveClient.isMicMuted();
  }

  async initLocalMicInput(deviceId: string | null): Promise<boolean> {
    if (!this.#agoraLiveClient) {
      return false;
    }
    const localMediaStream = getLiveLocalStream();
    if (!localMediaStream) {
      return false;
    }
    this.#localAgoraTrack = await this.#agoraLiveClient.initLocalTrack({
      microphoneId: deviceId ?? undefined,
      audioMediaStream: localMediaStream
    });
    return true;
  }

  async changeMicDevice(deviceId: string): Promise<boolean> {
    if (!this.#agoraLiveClient) {
      return false;
    }
    await this.#agoraLiveClient.changeInputDevices({
      microphoneId: deviceId
    });
    closeLiveLocalStream();
    this.#setLocalStreamFromAgoraLocalTrack();
    if (this.getLiveMicEnabled()) {
      // detectのmaxDomainが0になるため、止めてから再開する
      setLiveMicEnabled(false);
      setLiveMicEnabled(true);
    }
    return true;
  }

  getGenericRtcTokenType(): Exclude<
    Parameters<typeof globalThis.heliport.v3.api.genericRtcToken.getToken>[1],
    undefined
  > {
    return 'agora';
  }

  initializeLiveVoice(): void {
    this.#setLocalStreamFromAgoraLocalTrack();
  }
  #setLocalStreamFromAgoraLocalTrack(): void {
    if (!this.#agoraLiveClient) {
      return;
    }
    const localMediaStream = this.#agoraLiveClient.getLocalMediaStream();
    setLiveLocalStream(localMediaStream);
  }
  async joinLiveChannelAsHostWithToken(
    token: Extract<
      Awaited<ReturnType<typeof globalThis.heliport.v3.api.genericRtcToken.getToken>>,
      {status: 'ok'}
    >['data']
  ): Promise<void> {
    if (!token.room_id) {
      throw new Error('The token.room_id is undefined.');
    }

    // TODO: agoraUserID と CRP objectIDの変換テーブル
    const agoraUserId = token.user_id ? parseInt(token.user_id) : null;

    const micEnabled = false;
    if (!this.#agoraLiveClient) {
      throw new Error('agoraLiveClient is null.');
    }
    this.#agoraLiveClient.setLocalAgoraTrack(this.#localAgoraTrack);
    await this.#agoraLiveClient.joinLiveHostByToken(
      token.app_id,
      token.token,
      token.room_id,
      agoraUserId,
      micEnabled
    );
  }

  async joinLiveChannelAsAudienceWithToken(
    token: Extract<
      Awaited<ReturnType<typeof globalThis.heliport.v3.api.genericRtcToken.getToken>>,
      {status: 'ok'}
    >['data']
  ): Promise<void> {
    if (!token.room_id) {
      throw new Error('The token.room_id is undefined.');
    }

    const agoraUserId = token.user_id ? parseInt(token.user_id) : null;
    const latencyLevel = 1;

    if (!this.#agoraLiveClient) {
      throw new Error('agoraLiveClient is null.');
    }
    await this.#agoraLiveClient.joinLiveAudienceByToken(
      token.app_id,
      token.token,
      token.room_id,
      agoraUserId,
      latencyLevel
    );
  }

  async leaveLiveChannel(): Promise<void> {
    if (this.#agoraLiveClient) {
      await this.#agoraLiveClient.close();
    }
  }

  muteLiveStreamAudio(): void {
    if (!this.#agoraLiveClient) {
      throw new Error('agoraLiveClient is null.');
    }
    this.#agoraLiveClient.stopLiveStreamAudio();
  }

  unmuteLiveStreamAudio(): void {
    if (!this.#agoraLiveClient) {
      throw new Error('agoraLiveClient is null.');
    }
    this.#agoraLiveClient.playLiveStreamAudio();
  }

  isConnectedLiveChannel(liveChannel: CHeliLiveChannel): boolean {
    return this.#agoraLiveClient &&
      this.#agoraLiveClient.ChannelName() === `live__${liveChannel.getChannelId()}`
      ? true
      : false;
  }
  isLiveChannelPresenter(liveChannel: CHeliLiveChannel): boolean {
    return this.isConnectedLiveChannel(liveChannel) && this.#agoraLiveClient?.getRole() === 'host';
  }
}
